import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface VideoContextType {
    isPlaying: boolean;
    togglePlay: () => void;
    play: () => void;
    pause: () => void;
    seek: (time: number) => void;
    registerVideoPlayers: (mainVideo: any, webcamVideo: any) => void;
    duration: number, // Duration of the video
}

const VideoContext = createContext<VideoContextType>({
    isPlaying: false,
    togglePlay: () => { },
    play: () => { },
    pause: () => { },
    seek: () => { },
    registerVideoPlayers: () => { },
    duration: 0, // Default duration
});

export const useVideoContext = () => useContext(VideoContext);

export const VideoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [mainVideo, setMainVideo] = useState<any>(null);
    const [webcamVideo, setWebcamVideo] = useState<any>(null);
    const [duration, setDuration] = useState<number>(0); // State for duration

    const registerVideoPlayers = useCallback((main: any, webcam: any) => {
        
        setMainVideo(main);
        setWebcamVideo(webcam);
    }, []);

    // Update duration when mainVideo is set or changes
    useEffect(() => {
        
        if (mainVideo && typeof mainVideo.getDuration === 'function') {
            // Get initial duration
            try {
                const videoDuration = mainVideo.getDuration();
                
                if (videoDuration && videoDuration > 0) {                    
                    setDuration(videoDuration);
                }
                
            } catch (err) {
                console.error('Error getting video duration:', err);
            }

            // Set up a listener for when the video is ready/loaded
            const handleReady = () => {
                try {
                    const videoDuration = mainVideo.getDuration();
                    
                    if (videoDuration && videoDuration > 0) {
                        
                        setDuration(videoDuration);
                    }
                } catch (err) {
                    console.error('Error getting video duration on ready:', err);
                }
            };

            // Listen for ready or loaded events
            mainVideo.bind('ready', handleReady);
            mainVideo.bind('loaded', handleReady);
            mainVideo.bind('loadProgress', handleReady);

            return () => {
                mainVideo.unbind('ready', handleReady);
                mainVideo.unbind('loaded', handleReady);
                mainVideo.unbind('loadProgress', handleReady);
            };
        }
        
    }, [mainVideo]);

    const play = () => {
        if (mainVideo) {
            mainVideo.play();
        }
        // Webcam playback is already synchronized in VideoEditorMain component
        setIsPlaying(true);
    };

    const pause = () => {
        if (mainVideo) {
            mainVideo.pause();
        }
        if (webcamVideo) {
            webcamVideo.pause();
        }
        setIsPlaying(false);
    };

    const togglePlay = () => {
        if (isPlaying) {
            pause();
        } else {
            setDuration(mainVideo.getDuration());
            play();
        }
    };

    const seek = (time: number) => {
        if (mainVideo) {
            mainVideo.seek(time);
        }
        if (webcamVideo) {
            webcamVideo.seek(time);
        }
    };

    // Listen for play/pause events from video players
    useEffect(() => {
        if (mainVideo) {
            const handlePlay = () => setIsPlaying(true);
            const handlePause = () => setIsPlaying(false);

            mainVideo.bind("play", handlePlay);
            mainVideo.bind("pause", handlePause);

            return () => {
                mainVideo.unbind("play", handlePlay);
                mainVideo.unbind("pause", handlePause);
            };
        }
    }, [mainVideo]);

    return (
        <VideoContext.Provider
            value={{
                isPlaying,
                togglePlay,
                play,
                pause,
                seek,
                registerVideoPlayers,
                duration
            }}
        >
            {children}
        </VideoContext.Provider>
    );
};